/**
 * API Client with Impersonation Support
 *
 * Wraps fetch() to automatically add impersonation headers when support is impersonating.
 * Use this instead of direct fetch() calls for all API requests.
 *
 * Usage:
 * import { apiClient } from '../utils/apiClient';
 *
 * const response = await apiClient.fetch('/api/services', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'My Service' })
 * });
 */

import { auth } from '../firebase/config';
import { showSubscriptionToast, showLimitToast, show403Toast } from './subscriptionToast';
import { getAuthHeaders } from './authHeaders';
import { toast } from 'react-toastify';

class APIClient {
  constructor() {
    this.impersonation = null;
  }

  /**
   * Set impersonation context
   * Called by AuthContext when impersonation starts/ends
   */
  setImpersonation(impersonationState) {
    this.impersonation = impersonationState;
  }

  /**
   * Enhanced fetch that adds impersonation headers
   */
  async fetch(url, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Add authentication token
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add impersonation headers if actively impersonating
    if (this.impersonation?.isImpersonating) {
      headers['X-Impersonate-User'] = this.impersonation.targetUserId;
      headers['X-Impersonation-Session'] = this.impersonation.sessionId;

      console.log(`🔐 API call with impersonation: ${url}`, {
        adminId: this.impersonation.adminId,
        targetUserId: this.impersonation.targetUserId
      });
    }

    return fetch(url, {
      ...options,
      headers
    });
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  async get(url, options = {}) {
    return this.fetch(url, { ...options, method: 'GET' });
  }

  async post(url, data, options = {}) {
    return this.fetch(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(url, data, options = {}) {
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(url, options = {}) {
    return this.fetch(url, { ...options, method: 'DELETE' });
  }

  async patch(url, data, options = {}) {
    return this.fetch(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }
}

// Export singleton instance
export const apiClient = new APIClient();

// ============================================================================
// Centralized 403/402 Interceptor
// ============================================================================

/**
 * Shared 403 dispatch logic — used by both fetch and Axios paths.
 * Inspects the parsed error body and shows the appropriate toast.
 */
export function dispatch403Toast(errorData) {
  if (errorData?.feature) {
    showSubscriptionToast(errorData.feature, errorData.currentPlan);
  } else if (errorData?.limitType || errorData?.limit !== undefined) {
    showLimitToast(
      errorData.limitType || 'appointments',
      errorData.limit,
      errorData.current || null
    );
  } else {
    // Backend returned a 402/403 with no actionable feature metadata.
    // Silent — log to console, don't spam the user. Dashboard tiles
    // that passively load paid endpoints shouldn't scream on every
    // open. Only explicit user actions (handled by their own catch
    // blocks) should raise a visible upgrade prompt.
    // eslint-disable-next-line no-console
    console.warn('[dispatch403Toast] suppressed — no feature in errorData', errorData);
  }
}

/**
 * Drop-in replacement for all per-service `apiRequest` functions.
 *
 * @param {string} endpoint  - Path appended to baseUrl (e.g. '/bookings')
 * @param {Object} options   - fetch() options. May include `suppressSubscriptionToast`.
 * @param {string} baseUrl   - Base API URL (defaults to REACT_APP_SECURE_API_URL)
 * @returns {Promise<any>}   - Parsed JSON body
 */
export async function authenticatedFetch(endpoint, options = {}, baseUrl) {
  const base = baseUrl || process.env.REACT_APP_SECURE_API_URL ||
    'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';
  const fullUrl = `${base}${endpoint}`;

  // Strip non-fetch option before spreading
  const { suppressSubscriptionToast, ...fetchOptions } = options;

  const headers = await getAuthHeaders(fullUrl);

  const response = await fetch(fullUrl, {
    ...fetchOptions,
    headers: {
      ...headers,
      ...fetchOptions.headers
    }
  });

  // 402 — credits / feature-locked
  if (response.status === 402) {
    const data = await response.json().catch(() => ({}));
    if (!suppressSubscriptionToast) {
      if (data.code === 'INSUFFICIENT_CREDITS') {
        toast.warning(data.message || 'Insufficient credits for this operation', {
          autoClose: 6000, closeButton: true, position: 'top-center'
        });
      } else if (data.feature) {
        // Only show a feature-gate toast when the backend actually names the feature.
        showSubscriptionToast(data.feature, data.currentPlan);
      } else {
        // eslint-disable-next-line no-console
        console.warn('[authenticatedFetch 402] suppressed — no feature in response', data);
      }
    }
    const error = new Error(data.message || `This requires ${data.required || 'more'} credits. Your balance: ${data.balance || 0}`);
    error.isCreditsError = true;
    error.status = 402;
    error.required = data.required;
    error.balance = data.balance;
    error.operation = data.operation;
    throw error;
  }

  // 403 — subscription / feature / limit gate
  if (response.status === 403) {
    let errorData = {};
    try { errorData = await response.json(); } catch (_) {}
    if (!suppressSubscriptionToast) {
      dispatch403Toast(errorData);
    }
    const error = new Error('Subscription required');
    error.isSubscriptionError = true;
    error.status = 403;
    throw error;
  }

  // Other errors
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorBody.error || errorBody.message || `API error: ${response.statusText}`);
  }

  return await response.json();
}
